"use client";

import {
  addDays,
  addMonths,
  format,
  getDay,
  getDaysInMonth,
  parse,
  startOfMonth,
} from "date-fns-jalali";
import {
  addMonths as addGregorianMonths,
  format as formatGregorian,
  getDaysInMonth as getGregorianDaysInMonth,
  startOfMonth as startOfGregorianMonth,
} from "date-fns";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PhosphorIcon from "@/shared/components/common/PhosphorIcon";
import { createRoomsPattern } from "@/shared/libs/utils";
import { useHotelStore } from "@/shared/stores/hotel-store/HotelStoreProvider";
import type { HotelDetailV2BookingModel } from "../model/hotelDetailV2.model";
import {
  createHotelPassengersUrl,
  getSelectedRoomsCount,
  getSelectedRoomsTotal,
} from "../utils/bookingNavigation";

interface BookingPanelProps {
  booking: HotelDetailV2BookingModel;
}

export default function BookingPanel({ booking }: BookingPanelProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [checkIn, setCheckIn] = useState(booking.checkIn.replaceAll("-", "/"));
  const [checkOut, setCheckOut] = useState(booking.checkOut.replaceAll("-", "/"));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [draftAdults, setDraftAdults] = useState(booking.adults);
  const [draftChildren, setDraftChildren] = useState(booking.children);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setCheckIn(booking.checkIn.replaceAll("-", "/"));
      setCheckOut(booking.checkOut.replaceAll("-", "/"));
      setDraftAdults(booking.adults);
      setDraftChildren(booking.children);
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [booking.adults, booking.checkIn, booking.checkOut, booking.children]);

  const originalRooms = useMemo(
    () => createRoomsPattern("decode", booking.roomsParam || "2"),
    [booking.roomsParam],
  );
  const draftNights = getDraftNights(checkIn, checkOut);
  const hasDraftChanges =
    checkIn !== booking.checkIn.replaceAll("-", "/") ||
    checkOut !== booking.checkOut.replaceAll("-", "/") ||
    draftAdults !== booking.adults ||
    draftChildren !== booking.children;

  const replaceQuery = (changes: Record<string, string>) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(changes).forEach(([key, value]) => params.set(key, value));
    window.dispatchEvent(new Event("hotel-detail-v2:rooms-loading"));
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  const applyDraft = () => {
    const normalizedCheckIn = checkIn.replaceAll("/", "-");
    const normalizedCheckOut = checkOut.replaceAll("/", "-");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedCheckIn)) {
      setCheckIn(booking.checkIn.replaceAll("-", "/"));
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedCheckOut)) {
      setCheckOut(booking.checkOut.replaceAll("-", "/"));
      return;
    }

    const nextRooms = originalRooms.map((room, index) =>
      index === 0
        ? {
            ...room,
            adult: Math.max(booking.roomCount, draftAdults),
            child: Array.from({ length: draftChildren }, () => "6"),
          }
        : room,
    );
    replaceQuery({
      checkIn: normalizedCheckIn,
      checkOut: normalizedCheckOut,
      rooms: createRoomsPattern("encode", nextRooms),
    });
  };

  const scrollToRooms = () => {
    document.getElementById("rooms")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleRoomsCta = () => {
    if (!hasDraftChanges) return;

    if (hasDraftChanges) {
      applyDraft();
    }
    scrollToRooms();
  };

  return (
    <div className="space-y-3">
      <div className="overflow-hidden rounded-2xl border border-[rgb(229,231,235)] bg-white shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
        {/* TODO: Restore sidebar nightly price summary after the final pricing UX is approved. */}

        <div className="space-y-2.5 p-4">
          <button
            type="button"
            onClick={() => setCalendarOpen(true)}
            className="block w-full rounded-lg border border-[rgb(229,231,235)] p-3 text-right transition hover:border-primary"
          >
            <span className="mb-1 flex items-center gap-1 text-[11px] text-[#6b7280]">
              <PhosphorIcon name="CalendarBlank" size={13} />
              تاریخ ورود و خروج
            </span>
            <span className="block text-sm font-extrabold text-[#1a1a2e]">
              {checkIn} تا {checkOut}
            </span>
            <span className="mt-1 block text-[11px] font-bold text-primary">
              {draftNights.toLocaleString("fa-IR")} شب اقامت
            </span>
          </button>

          {calendarOpen ? (
            <DateRangeModal
              checkIn={checkIn}
              checkOut={checkOut}
              onClose={() => setCalendarOpen(false)}
              onConfirm={(nextCheckIn, nextCheckOut) => {
                setCheckIn(nextCheckIn);
                setCheckOut(nextCheckOut);
                setCalendarOpen(false);
              }}
            />
          ) : null}

          <div className="rounded-lg border border-[rgb(229,231,235)] p-2.5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] text-[#6b7280]">مهمان</p>
                <p className="text-sm font-bold text-[#1a1a2e]">
                  {draftAdults.toLocaleString("fa-IR")} بزرگسال
                  {draftChildren > 0
                    ? `، ${draftChildren.toLocaleString("fa-IR")} کودک`
                    : ""}
                </p>
              </div>
              <Stepper
                value={draftAdults}
                min={booking.roomCount}
                max={8 * booking.roomCount}
                onChange={(value) =>
                  setDraftAdults(
                    Math.min(8 * booking.roomCount, Math.max(booking.roomCount, value)),
                  )
                }
                label="بزرگسال"
              />
            </div>
            <p className="mt-1 text-[11px] text-[#9ca3af]">
              {booking.roomCount.toLocaleString("fa-IR")} اتاق
            </p>
          </div>

          {/* TODO: Restore child selector after child age/pricing UX is finalized. */}

          {/* TODO: Restore sidebar total price block after final price copy/layout is approved. */}

          <button
            type="button"
            onClick={handleRoomsCta}
            disabled={!hasDraftChanges}
            className={`flex h-11 w-full items-center justify-center rounded-lg border text-sm font-bold transition ${
              hasDraftChanges
                ? "border-primary bg-primary text-white hover:opacity-90"
                : "cursor-default border-[#e5e7eb] bg-white text-[#6b7280]"
            }`}
            data-analytics-event="cta_click"
            data-analytics-id="hotel-detail-v2-view-rooms"
            data-analytics-name="مشاهده اتاق‌های موجود"
            data-analytics-type="button"
            data-analytics-funnel="hotel_booking"
            data-analytics-step="detail_view_rooms_cta"
            data-analytics-property-check-in={booking.checkIn}
            data-analytics-property-check-out={booking.checkOut}
            data-analytics-property-room-count={String(booking.roomCount)}
            data-analytics-property-nightly-price={String(booking.nightlyPrice ?? "")}
          >
            مشاهده اتاق‌های موجود
          </button>
        </div>
      </div>
    </div>
  );
}

export function SelectedRoomsCard({
  booking,
  city,
  slug,
}: {
  booking: HotelDetailV2BookingModel;
  city: string;
  slug: string;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { rooms, setDetailRooms } = useHotelStore((state) => state);
  const selectedCount = getSelectedRoomsCount(rooms);
  const selectedTotal = getSelectedRoomsTotal(rooms);
  const canContinue = selectedCount > 0 && Boolean(booking.accommodationKey);

  if (selectedCount === 0) return null;

  const handleSubmit = () => {
    if (!canContinue) return;

    router.push(
      createHotelPassengersUrl({
        city,
        slug,
        booking,
        selectedRooms: rooms,
        currentQuery: searchParams.toString(),
      }),
    );
  };

  return (
    <section className="rounded-2xl border border-[rgb(229,231,235)] bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-sm font-extrabold text-[#1a1a2e]">
          اتاق‌های انتخاب‌شده
        </h2>
        <span className="rounded-full bg-primary/5 px-2.5 py-1 text-[11px] font-bold text-primary">
          {selectedCount.toLocaleString("fa-IR")} اتاق
        </span>
      </div>

      <div className="space-y-2">
        {rooms.map((room) => (
          <div
            key={`${room.id}-${room.rate_plans.id}`}
            className="rounded-lg border border-[#eef0f3] p-2.5"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="line-clamp-2 text-xs font-bold leading-5 text-[#1a1a2e]">
                {room.name}
              </p>
              <button
                type="button"
                onClick={() =>
                  setDetailRooms(
                    rooms.filter(
                      (item) =>
                        !(
                          item.id === room.id &&
                          item.rate_plans.id === room.rate_plans.id
                        ),
                    ),
                  )
                }
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-[#e5e7eb] text-[#6b7280] transition hover:border-primary hover:text-primary"
                aria-label={`حذف ${room.name}`}
              >
                <PhosphorIcon name="X" size={13} />
              </button>
            </div>
            <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-[#6b7280]">
              <span>{room.rate_plans.name}</span>
              <span className="shrink-0">
                {room.count.toLocaleString("fa-IR")} ×{" "}
                {room.rate_plans.price.toLocaleString("fa-IR")}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 border-t border-[#eef0f3] pt-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-[#6b7280]">مجموع قیمت</span>
          <strong className="text-base font-extrabold text-[#1a1a2e]">
            {selectedTotal.toLocaleString("fa-IR")} تومان
          </strong>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canContinue}
          className="mt-3 flex h-11 w-full items-center justify-center rounded-lg bg-primary text-sm font-bold text-white transition hover:opacity-90 disabled:bg-slate-200 disabled:text-slate-500"
          data-analytics-event="cta_click"
          data-analytics-id="hotel-detail-v2-sidebar-submit-passengers"
          data-analytics-name="ثبت اطلاعات"
          data-analytics-type="button"
          data-analytics-funnel="hotel_booking"
          data-analytics-step="sidebar_submit_passengers"
          data-analytics-property-city={city}
          data-analytics-property-hotel-slug={slug}
          data-analytics-property-selected-count={String(selectedCount)}
          data-analytics-property-selected-total={String(selectedTotal)}
        >
          ثبت اطلاعات
        </button>
      </div>
    </section>
  );
}

export function DateRangeModal({
  checkIn,
  checkOut,
  onClose,
  onConfirm,
  title = "انتخاب تاریخ اقامت",
  checkInLabel = "ورود",
  checkOutLabel = "خروج",
  checkInHelper = "تاریخ ورود را انتخاب کنید",
  checkOutHelper = "حالا تاریخ خروج را انتخاب کنید",
  showSelectedSummary = true,
  showCalendarToggle = true,
  allowPastDates = false,
  reverseNavigationIcons = false,
  minSelectableDate,
  maxSelectableDate,
  allowSameDayRange = false,
}: {
  checkIn: string;
  checkOut: string;
  onClose: () => void;
  onConfirm: (checkIn: string, checkOut: string) => void;
  title?: string;
  checkInLabel?: string;
  checkOutLabel?: string;
  checkInHelper?: string;
  checkOutHelper?: string;
  showSelectedSummary?: boolean;
  showCalendarToggle?: boolean;
  allowPastDates?: boolean;
  reverseNavigationIcons?: boolean;
  minSelectableDate?: string;
  maxSelectableDate?: string;
  allowSameDayRange?: boolean;
}) {
  const initialCheckIn = parseJalaliDate(checkIn);
  const initialCheckOut = parseJalaliDate(checkOut);
  const minDate = minSelectableDate ? parseJalaliDate(minSelectableDate) : null;
  const maxDate = maxSelectableDate ? parseJalaliDate(maxSelectableDate) : null;
  const [calendar, setCalendar] = useState<"jalali" | "gregorian">("jalali");
  const [viewDate, setViewDate] = useState(initialCheckIn);
  const [pendingCheckIn, setPendingCheckIn] = useState<Date | null>(initialCheckIn);
  const [pendingCheckOut, setPendingCheckOut] = useState<Date | null>(initialCheckOut);
  const [step, setStep] = useState<"checkin" | "checkout">("checkin");
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [todayBlink, setTodayBlink] = useState(false);
  const [mounted, setMounted] = useState(false);
  const confirmActionsRef = useRef<HTMLDivElement>(null);
  const isJalali = calendar === "jalali";
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const getNextMonth = (date: Date) =>
    isJalali ? addMonths(date, 1) : addGregorianMonths(date, 1);
  const goPrev = () =>
    setViewDate((current) =>
      isJalali ? addMonths(current, -1) : addGregorianMonths(current, -1),
    );
  const goNext = () =>
    setViewDate((current) =>
      isJalali ? addMonths(current, 1) : addGregorianMonths(current, 1),
    );

  const handleSelect = (date: Date) => {
    if (step === "checkin") {
      setPendingCheckIn(date);
      setPendingCheckOut(null);
      setStep("checkout");
      return;
    }

    if (
      !pendingCheckIn ||
      date < pendingCheckIn ||
      (!allowSameDayRange && sameDay(date, pendingCheckIn))
    ) {
      setPendingCheckIn(date);
      setPendingCheckOut(null);
      setStep("checkout");
      return;
    }

    setPendingCheckOut(date);
    setStep("checkin");
    window.setTimeout(() => {
      confirmActionsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 80);
  };

  const goToday = () => {
    const today = new Date();
    setViewDate(today);
    setTodayBlink(false);
    window.setTimeout(() => setTodayBlink(true), 20);
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => setMounted(true), 0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.clearTimeout(timeout);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!todayBlink) {
      return;
    }

    const timeout = window.setTimeout(() => setTodayBlink(false), 1900);
    return () => window.clearTimeout(timeout);
  }, [todayBlink]);

  const canConfirm = Boolean(pendingCheckIn && pendingCheckOut);
  const helperText =
    step === "checkin" || !pendingCheckIn
      ? checkInHelper
      : checkOutHelper;

  const renderMonth = (monthDate: Date) => {
    const monthStart = isJalali
      ? startOfMonth(monthDate)
      : startOfGregorianMonth(monthDate);
    const daysCount = isJalali
      ? getDaysInMonth(monthDate)
      : getGregorianDaysInMonth(monthDate);
    const offset = (getDay(monthStart) + 1) % 7;
    const days = Array.from({ length: daysCount }, (_, index) =>
      addDays(monthStart, index),
    );
    const headerLabel = isJalali
      ? format(monthDate, "MMMM yyyy")
      : formatGregorian(monthDate, "MMM yyyy");

    return (
      <section className="rounded-2xl border border-[rgb(229,231,235)] p-3 sm:p-4">
        <p className="mb-3 text-center text-base font-extrabold text-[#1a1a2e]">
          {headerLabel}
        </p>

        <div className="mb-2 grid grid-cols-7 text-center text-xs font-bold text-[#6b7280]">
          {["ش", "ی", "د", "س", "چ", "پ", "ج"].map((day) => (
            <span key={day} className="py-1">
              {day}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: offset }).map((_, index) => (
            <span key={`empty-${index}`} />
          ))}
          {days.map((date) => {
            const active =
              (pendingCheckIn && sameDay(date, pendingCheckIn)) ||
              (pendingCheckOut && sameDay(date, pendingCheckOut));
            const inRange =
              Boolean(pendingCheckIn && pendingCheckOut) &&
              date > pendingCheckIn! &&
              date < pendingCheckOut!;
            const isPast = !allowPastDates && date < todayStart;
            const isBeforeMin = Boolean(minDate && date < minDate);
            const isAfterMax = Boolean(maxDate && date > maxDate);
            const isDisabled = isPast || isBeforeMin || isAfterMax;
            const isToday = sameDay(date, todayStart);
            const key = formatGregorian(date, "yyyy-MM-dd");
            const tooltip =
              step === "checkin" || !pendingCheckIn ? checkInHelper : checkOutHelper;

            return (
              <button
                key={key}
                onClick={() => handleSelect(date)}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
                disabled={isDisabled}
                className={`relative grid h-10 place-items-center rounded-lg border text-sm transition sm:h-11 ${
                  active
                    ? "border-primary bg-primary font-extrabold text-white"
                    : inRange
                      ? "border-primary/10 bg-primary/5 font-bold text-primary"
                      : isDisabled
                        ? "cursor-not-allowed border-transparent text-[#cbd5e1]"
                        : isToday && todayBlink
                          ? "hotel-detail-today-blink border-primary text-[#1a1a2e]"
                          : "border-transparent text-[#1a1a2e] hover:border-[rgb(229,231,235)] hover:bg-[#f5f6f8]"
                }`}
                type="button"
              >
                {isJalali ? format(date, "d") : date.getDate()}
                {hoveredKey === key && !isDisabled ? (
                  <span className="pointer-events-none absolute -top-8 right-1/2 z-10 translate-x-1/2 whitespace-nowrap rounded-md bg-[#1a1a2e] px-2 py-1 text-[11px] font-bold text-white shadow-lg">
                    {tooltip}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const modal = (
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-[#6b728045] p-3 backdrop-blur-[1.5px] sm:p-5">
      <style>
        {`
          @keyframes hotelDetailTodayBlink {
            0%, 100% { border-color: var(--primary); box-shadow: 0 0 0 0 color-mix(in oklch, var(--primary) 0%, transparent); }
            50% { border-color: var(--primary); box-shadow: 0 0 0 4px color-mix(in oklch, var(--primary) 28%, transparent); }
          }
          .hotel-detail-today-blink {
            animation: hotelDetailTodayBlink 0.45s ease-in-out 4;
          }
        `}
      </style>
      <button
        aria-label="بستن تقویم"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
        type="button"
      />
      <div className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl bg-white shadow-[0_24px_70px_rgba(15,23,42,0.28)]">
        <div className="flex items-start justify-between gap-3 border-b border-[rgb(229,231,235)] px-4 py-3 sm:px-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                {title ? (
                  <p className="text-base font-extrabold text-[#1a1a2e] sm:text-lg">{title}</p>
                ) : null}
                <p className="mt-0.5 text-xs font-bold text-primary">{helperText}</p>
              </div>
              {showSelectedSummary ? (
              <div className="grid grid-cols-2 gap-2 lg:min-w-[360px]">
                <div className="rounded-lg border border-[rgb(229,231,235)] bg-[#f9fafb] px-3 py-2 text-right">
                  <span className="block text-[10px] text-[#6b7280]">{checkInLabel}</span>
                  <span className="mt-0.5 block text-xs font-extrabold text-[#1a1a2e] sm:text-sm">
                    {pendingCheckIn ? format(pendingCheckIn, "yyyy/MM/dd") : "انتخاب نشده"}
                  </span>
                </div>
                <div className="rounded-lg border border-[rgb(229,231,235)] bg-[#f9fafb] px-3 py-2 text-right">
                  <span className="block text-[10px] text-[#6b7280]">{checkOutLabel}</span>
                  <span className="mt-0.5 block text-xs font-extrabold text-[#1a1a2e] sm:text-sm">
                    {pendingCheckOut ? format(pendingCheckOut, "yyyy/MM/dd") : "انتخاب نشده"}
                  </span>
                </div>
              </div>
              ) : null}
            </div>
          </div>
          <button
            aria-label="بستن"
            className="grid h-9 w-9 place-items-center rounded-full border border-[rgb(229,231,235)] text-[#6b7280] transition hover:bg-[#f5f6f8]"
            onClick={onClose}
            type="button"
          >
            <PhosphorIcon name="X" size={16} />
          </button>
        </div>

        <div className="max-h-[calc(92vh-96px)] overflow-y-auto p-4 sm:p-5">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <button
              onClick={goToday}
              className="rounded-lg border border-[rgb(229,231,235)] px-3 py-2 text-xs font-bold text-[#1a1a2e] transition hover:border-primary hover:text-primary"
              type="button"
            >
              برو به امروز
            </button>
            {showCalendarToggle ? (
              <button
              onClick={() => {
                setCalendar((value) =>
                  value === "jalali" ? "gregorian" : "jalali",
                );
                setViewDate(pendingCheckIn || new Date());
              }}
              className="rounded-lg border px-3 py-2 text-xs font-bold transition"
              style={{
                borderColor: isJalali ? "rgb(229,231,235)" : "var(--primary)",
                color: isJalali ? "#6b7280" : "var(--primary)",
              }}
              type="button"
            >
              {isJalali ? "تقویم میلادی" : "تقویم شمسی"}
            </button>
            ) : null}
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <button
                onClick={goPrev}
                type="button"
                className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-[#f5f6f8]"
              >
                <PhosphorIcon
                  name={reverseNavigationIcons ? "CaretLeft" : "CaretRight"}
                  size={17}
                  className="text-[#6b7280]"
                />
              </button>
              <p className="text-center text-base font-extrabold text-[#1a1a2e]">
                {isJalali ? "تقویم شمسی" : "تقویم میلادی"}
              </p>
              <button
                onClick={goNext}
                type="button"
                className="grid h-9 w-9 place-items-center rounded-full transition hover:bg-[#f5f6f8]"
              >
                <PhosphorIcon
                  name={reverseNavigationIcons ? "CaretRight" : "CaretLeft"}
                  size={17}
                  className="text-[#6b7280]"
                />
              </button>
            </div>

            <div className="grid gap-3 lg:grid-cols-2">
              {renderMonth(viewDate)}
              {renderMonth(getNextMonth(viewDate))}
            </div>
          </div>

          <div
            ref={confirmActionsRef}
            className="mt-4 flex flex-col-reverse gap-2 border-t border-[rgb(229,231,235)] pt-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-[rgb(229,231,235)] px-5 text-sm font-bold text-[#6b7280] transition hover:bg-[#f5f6f8]"
            >
              انصراف
            </button>
            <button
              type="button"
              disabled={!canConfirm}
              onClick={() => {
                if (!pendingCheckIn || !pendingCheckOut) {
                  return;
                }
                onConfirm(
                  format(pendingCheckIn, "yyyy/MM/dd"),
                  format(pendingCheckOut, "yyyy/MM/dd"),
                );
              }}
              className="h-11 rounded-xl bg-primary px-6 text-sm font-extrabold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-slate-200"
            >
              تایید تاریخ
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!mounted) {
    return null;
  }

  return createPortal(modal, document.body);
}

function parseJalaliDate(value: string) {
  return parse(value.replaceAll("/", "-"), "yyyy-MM-dd", new Date());
}

function sameDay(first: Date, second: Date) {
  return formatGregorian(first, "yyyy-MM-dd") === formatGregorian(second, "yyyy-MM-dd");
}

function getDraftNights(checkIn: string, checkOut: string) {
  const start = parseJalaliDate(checkIn);
  const end = parseJalaliDate(checkOut);
  const diff = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  return Math.max(1, diff);
}

function Stepper({
  value,
  min,
  max,
  onChange,
  label,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(value - 1)}
        disabled={value <= min}
        aria-label={`کاهش ${label}`}
        className="grid h-7 w-7 place-items-center rounded-full border border-[rgb(229,231,235)] disabled:opacity-35"
      >
        <PhosphorIcon name="Minus" size={12} />
      </button>
      <span className="min-w-4 text-center text-sm font-bold">{value.toLocaleString("fa-IR")}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        disabled={value >= max}
        aria-label={`افزایش ${label}`}
        className="grid h-7 w-7 place-items-center rounded-full border border-[rgb(229,231,235)] disabled:opacity-35"
      >
        <PhosphorIcon name="Plus" size={12} />
      </button>
    </div>
  );
}
